/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving and saving a depot batch.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDepotBatchState {
    depotBatch: Dtos.DepotBatch | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
    saveState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDepotBatchState {
    depotBatch: IInitialDepotBatchState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DepotBatchInitialState: IInitialDepotBatchState = {
    depotBatch: null,
    loadState: {
        state: RequestState.None
    },
    saveState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDepotBatchSelector: TypedUseSelectorHook<IDepotBatchState> = useSelector;

export const DepotBatchSelectors = {
    selectDepotBatch: (state: IDepotBatchState) => state.depotBatch.depotBatch,
    selectLoadState: (state: IDepotBatchState) => state.depotBatch.loadState,
    selectSaveState: (state: IDepotBatchState) => state.depotBatch.saveState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DepotBatchReducer extends ImmerReducer<IInitialDepotBatchState> {
    public loadById(id: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(depotBatch?: Dtos.DepotBatch) {
        this.draftState.depotBatch = depotBatch ? depotBatch : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.depotBatch = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public save(form: Dtos.DepotBatch) {
        this.draftState.saveState = {
            state: RequestState.Pending
        };
    }

    public saveSuccess(depotBatch?: Dtos.DepotBatch) {
        this.draftState.depotBatch = depotBatch ? depotBatch : null;
        this.draftState.saveState = {
            state: RequestState.Success
        };
    }

    public saveFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.depotBatch = null;
        this.draftState.saveState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DepotBatchInitialState };
    }
};

export const DepotBatchActions = createActionCreators(DepotBatchReducer);
const reducer = createReducerFunction(DepotBatchReducer, DepotBatchInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DepotBatchApi = {
    loadById: (id: number) => {
        return client.get(new Dtos.GetDepotBatchById({
            id
        }));
    },
    save: (depotBatch?: Dtos.DepotBatch) => {
        return client.post(new Dtos.SetDepotBatch({
            depotBatch
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DepotBatchLogic = {
    loadById: createLogic<IDepotBatchState, {}, undefined, string, ReturnType<typeof DepotBatchActions.loadById>>({
        type: DepotBatchActions.loadById.type,
        process: async ({ action }, dispatch, done) => {
            const id = action.payload;

            try {
                const response = await DepotBatchApi.loadById(id);

                dispatch(DepotBatchActions.loadSuccess(
                    response.depotBatch
                ));
            }
            catch (error: any) {
                dispatch(DepotBatchActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    save: createLogic<IDepotBatchState, {}, undefined, string, ReturnType<typeof DepotBatchActions.save>>({
        type: DepotBatchActions.save.type,
        process: async ({ action }, dispatch, done) => {
            const depotBatch = action.payload;

            try {
                const response = await DepotBatchApi.save(depotBatch);

                dispatch(DepotBatchActions.saveSuccess(
                    response.depotBatch
                ));
            }
            catch (error: any) {
                dispatch(DepotBatchActions.saveFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DepotBatchLogic.loadById,
    DepotBatchLogic.save
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDepotBatch = (): [Dtos.DepotBatch | null, IRequestState<Dtos.ResponseStatus>, IRequestState<Dtos.ResponseStatus>, typeof DepotBatchActions] => {
    const depotBatch = useSelector(DepotBatchSelectors.selectDepotBatch);
    const loadState = useSelector(DepotBatchSelectors.selectLoadState);
    const saveState = useSelector(DepotBatchSelectors.selectSaveState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DepotBatchActions>(() => {
        return bindActionCreators(DepotBatchActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DepotBatchActions, dispatch));
    }, [dispatch, setActions]);

    return [
        depotBatch,
        loadState,
        saveState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('depotBatch', reducer as any, logics as any);